import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { ExternalLink, Plus, Pencil, Trash2, X, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = (process.env.REACT_APP_BACKEND_URL || window.location.origin);

const PLATFORMS = [
  {
    key: "facebook",
    label: "Facebook",
    color: "#1877F2",
    gradient: "from-[#1877F2] to-[#0C5DC7]",
    icon: (
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
      </svg>
    ),
  },
  {
    key: "instagram",
    label: "Instagram",
    color: "#E4405F",
    gradient: "from-[#F58529] via-[#DD2A7B] to-[#8134AF]",
    icon: (
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor">
        <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
      </svg>
    ),
  },
  {
    key: "linkedin",
    label: "LinkedIn",
    color: "#0A66C2",
    gradient: "from-[#0A66C2] to-[#004182]",
    icon: (
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
      </svg>
    ),
  },
  {
    key: "google_business",
    label: "Google Business",
    color: "#4285F4",
    gradient: "from-[#4285F4] via-[#EA4335] to-[#FBBC05]",
    icon: (
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
    ),
  },
  {
    key: "twitter",
    label: "X (Twitter)",
    color: "#000000",
    gradient: "from-[#1DA1F2] to-[#0C85D0]",
    icon: (
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
      </svg>
    ),
  },
  {
    key: "tiktok",
    label: "TikTok",
    color: "#010101",
    gradient: "from-[#69C9D0] via-[#010101] to-[#EE1D52]",
    icon: (
      <svg viewBox="0 0 24 24" className="h-7 w-7" fill="currentColor">
        <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/>
      </svg>
    ),
  },
];

export default function SocialConnectionsWidget({ clientId, token: tokenProp, readOnly = false }) {
  const { token: authToken } = useAuth();
  const token = tokenProp || authToken;
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ profile_name: "", profile_url: "", followers_count: 0 });
  const headers = { Authorization: `Bearer ${token}` };

  const fetchConnections = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/social-connections`, { headers, params: { client_id: clientId } });
      setConnections(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [clientId, token]);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  const getConnection = (platformKey) => connections.find(c => c.platform === platformKey && c.status === "connected");

  const openCreate = (platform) => {
    // For Facebook and Instagram, trigger OAuth flow
    if (platform.key === "facebook" || platform.key === "instagram") {
      handleMetaOAuth(platform);
      return;
    }
    setForm({ profile_name: "", profile_url: "", followers_count: 0 });
    setModal({ mode: "create", platform });
  };

  const handleMetaOAuth = async (platform) => {
    try {
      const params = clientId ? `?client_id=${clientId}` : "";
      const res = await axios.get(`${API}/api/oauth/facebook/authorize${params}`, { headers });
      if (res.data?.authorization_url) {
        window.location.href = res.data.authorization_url;
      }
    } catch (e) {
      const detail = e.response?.data?.detail || "Erreur OAuth Facebook";
      if (detail.includes("not configured")) {
        toast.error("OAuth Facebook non configure. Contactez votre administrateur pour configurer FACEBOOK_APP_ID et FACEBOOK_APP_SECRET.");
      } else {
        toast.error(detail);
      }
    }
  };

  const openEdit = (conn, platform) => {
    setForm({ profile_name: conn.profile_name, profile_url: conn.profile_url, followers_count: conn.followers_count || 0 });
    setModal({ mode: "edit", platform, connection: conn });
  };

  const handleSave = async () => {
    if (!form.profile_name || !form.profile_url) { toast.error("Remplissez tous les champs"); return; }
    try {
      if (modal.mode === "create") {
        await axios.post(`${API}/api/social-connections`, {
          client_id: clientId,
          platform: modal.platform.key,
          ...form,
          followers_count: parseInt(form.followers_count) || 0,
        }, { headers });
        toast.success(`${modal.platform.label} connecte`);
      } else {
        await axios.put(`${API}/api/social-connections/${modal.connection.id}`, {
          ...form,
          followers_count: parseInt(form.followers_count) || 0,
        }, { headers });
        toast.success(`${modal.platform.label} mis a jour`);
      }
      setModal(null);
      fetchConnections();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDisconnect = async (conn, platform) => {
    if (!window.confirm(`Deconnecter ${platform.label} ?`)) return;
    try {
      await axios.delete(`${API}/api/social-connections/${conn.id}`, { headers });
      toast.success(`${platform.label} deconnecte`);
      fetchConnections();
    } catch (e) { toast.error("Erreur"); }
  };

  const connectedCount = PLATFORMS.filter(p => getConnection(p.key)).length;
  const totalPlatforms = PLATFORMS.length;

  if (loading) return <div className="text-[#64748b] text-center py-8">Chargement...</div>;

  return (
    <div className="space-y-5" data-testid="social-connections-widget">
      {/* Score header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link2 className="h-5 w-5 text-[#0099CC]" />
          <span className="text-white font-semibold">Presence Reseaux Sociaux</span>
          <span className={`text-sm font-bold px-2.5 py-0.5 rounded-full ${
            connectedCount === totalPlatforms ? "bg-green-500/20 text-green-400" :
            connectedCount >= 2 ? "bg-yellow-500/20 text-yellow-400" :
            "bg-red-500/20 text-red-400"
          }`} data-testid="social-score">
            {connectedCount}/{totalPlatforms}
          </span>
        </div>
      </div>

      {/* Platform cards grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="social-cards-grid">
        {PLATFORMS.map(platform => {
          const conn = getConnection(platform.key);
          const isConnected = !!conn;

          return (
            <div
              key={platform.key}
              className={`relative rounded-xl border overflow-hidden transition-all ${
                isConnected
                  ? "bg-[#151B2B] border-[#2A3441]"
                  : "bg-[#0B0F19]/60 border-[#1E2736] border-dashed"
              }`}
              data-testid={`social-card-${platform.key}`}
            >
              {/* Colored top bar */}
              <div className={`h-1 w-full bg-gradient-to-r ${platform.gradient} ${isConnected ? "opacity-100" : "opacity-30"}`} />

              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div style={{ color: isConnected ? platform.color : "#4A5568" }} className="transition-colors">
                      {platform.icon}
                    </div>
                    <div>
                      <h4 className="text-white font-medium text-sm">{platform.label}</h4>
                      {isConnected ? (
                        <p className="text-[#94A3B8] text-xs truncate max-w-[160px]">{conn.profile_name}</p>
                      ) : (
                        <p className="text-[#4A5568] text-xs">Non connecte</p>
                      )}
                    </div>
                  </div>
                  {/* Status badge */}
                  <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                    isConnected ? "bg-green-500/15 text-green-400" : "bg-orange-500/15 text-orange-400"
                  }`}>
                    {isConnected && <span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" />}
                    {isConnected ? "Connecte" : "Non connecte"}
                  </div>
                </div>

                {/* Connected details */}
                {isConnected && (
                  <div className="mb-3 space-y-1">
                    <a href={conn.profile_url} target="_blank" rel="noreferrer" className="text-[#0099CC] text-xs hover:underline flex items-center gap-1">
                      <ExternalLink className="h-3 w-3" /> Voir le profil
                    </a>
                    {conn.followers_count > 0 && (
                      <p className="text-[#64748b] text-xs">{conn.followers_count.toLocaleString("fr-FR")} abonnes</p>
                    )}
                  </div>
                )}

                {/* Actions */}
                {!readOnly && (
                  <div className="flex gap-2">
                    {isConnected ? (
                      <>
                        <Button size="sm" variant="outline" onClick={() => openEdit(conn, platform)}
                          className="flex-1 border-[#2A3441] text-[#94A3B8] hover:text-white text-xs h-8"
                          data-testid={`social-edit-${platform.key}`}
                        >
                          <Pencil className="h-3 w-3 mr-1" /> Modifier
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDisconnect(conn, platform)}
                          className="border-red-500/30 text-red-400 hover:bg-red-500/10 text-xs h-8"
                          data-testid={`social-disconnect-${platform.key}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" onClick={() => openCreate(platform)}
                        className="w-full text-xs h-8" style={{ backgroundColor: platform.color }}
                        data-testid={`social-connect-${platform.key}`}
                      >
                        <Plus className="h-3 w-3 mr-1" /> Connecter
                      </Button>
                    )}
                  </div>
                )}

                {/* Read-only hint for disconnected */}
                {readOnly && !isConnected && (
                  <p className="text-orange-400/70 text-xs italic">Non connecte — contactez votre agence pour assistance.</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" data-testid="social-modal">
          <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] p-6 w-full max-w-md mx-4">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3">
                <div style={{ color: modal.platform.color }}>{modal.platform.icon}</div>
                <h3 className="text-white font-semibold">
                  {modal.mode === "create" ? `Connecter ${modal.platform.label}` : `Modifier ${modal.platform.label}`}
                </h3>
              </div>
              <button onClick={() => setModal(null)} className="text-[#64748b] hover:text-white">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-[#94A3B8] text-sm mb-1 block">Nom du profil</label>
                <input
                  type="text"
                  value={form.profile_name}
                  onChange={e => setForm(f => ({ ...f, profile_name: e.target.value }))}
                  placeholder="Ex: NGL SERVICES"
                  className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm"
                  data-testid="social-input-name"
                />
              </div>
              <div>
                <label className="text-[#94A3B8] text-sm mb-1 block">URL du profil</label>
                <input
                  type="url"
                  value={form.profile_url}
                  onChange={e => setForm(f => ({ ...f, profile_url: e.target.value }))}
                  placeholder={`https://${modal.platform.key === "google_business" ? "business.google.com" : modal.platform.key + ".com"}/...`}
                  className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm"
                  data-testid="social-input-url"
                />
              </div>
              <div>
                <label className="text-[#94A3B8] text-sm mb-1 block">Nombre d'abonnes</label>
                <input
                  type="number"
                  value={form.followers_count}
                  onChange={e => setForm(f => ({ ...f, followers_count: e.target.value }))}
                  placeholder="0"
                  className="w-full bg-[#0B0F19] text-white border border-[#2A3441] rounded-lg px-3 py-2 text-sm"
                  data-testid="social-input-followers"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <Button onClick={() => setModal(null)} variant="outline" className="flex-1 border-[#2A3441] text-[#94A3B8]">
                Annuler
              </Button>
              <Button onClick={handleSave} className="flex-1" style={{ backgroundColor: modal.platform.color }} data-testid="social-save-btn">
                {modal.mode === "create" ? "Connecter" : "Enregistrer"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
